// Incentive Rules — every number the incentive engine uses, editable by
// Superadmin / Admin / Department Administrator without a developer. Each save
// is a new version with a reason (audited); the server validates it
// (engine.normaliseRules: no overlapping slabs, weights add up to 100…).
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { Calculator, Gift, History, Plus, Scale, Settings2, Star, Trash2, ShieldAlert, Sparkles } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { errText, inr, perfApi, usePerfMutation, useRules, type Rules } from '../api';
import { Dialog, Field, inputCls, Panel } from './ui';

export function RulesPanel() {
  const { data, isLoading } = useRules();
  const [r, setR] = useState<Rules | null>(null);
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [example, setExample] = useState({ target: 2500000, sales: 5500000 });
  const [showSlabs, setShowSlabs] = useState(false);
  const save = usePerfMutation((v: { rules: Rules; note: string }) => perfApi.saveRules(v.rules, v.note));

  useEffect(() => {
    if (data) {
      const copy = structuredClone(data.rules);
      if (!copy.calcMode) copy.calcMode = 'excess_then_gst';
      if (copy.gstPercent === undefined) copy.gstPercent = 18;
      if (copy.incentiveRatePct === undefined) copy.incentiveRatePct = 1.0;
      if (!copy.excludedUsernames) copy.excludedUsernames = ['bdm6'];
      setR(copy);
    }
  }, [data]);

  if (isLoading || !r || !data) return <div className="flex justify-center py-16"><Spinner /></div>;
  const ro = !data.canEdit;
  const dirty = JSON.stringify(r) !== JSON.stringify(data.rules);
  const set = <K extends keyof Rules>(k: K, v: Rules[K]) => setR({ ...r, [k]: v });
  const num = (v: string) => (v === '' ? 0 : Number(v));
  const weightTotal = Object.values(r.scoreWeights).reduce((a, b) => a + Number(b || 0), 0);
  const numInput = (value: number, onChange: (n: number) => void, extra = '') => (
    <input className={`${inputCls} ${extra}`} disabled={ro} type="number" value={value} onChange={(e) => onChange(num(e.target.value))} />
  );

  const calcMode = r.calcMode || 'excess_then_gst';
  const gstPercent = r.gstPercent ?? 18;
  const gstFactor = r.salesBasis === 'taxable' ? 1 : 1 + gstPercent / 100;
  const ratePct = r.incentiveRatePct ?? 1.0;

  // Worked example calculation based on selected mode
  const pct = example.target ? Math.round((example.sales / example.target) * 1000) / 10 : 0;
  const slab = r.slabs.find((s) => pct >= s.fromPct && (s.toPct === null || pct < s.toPct));

  let exBase = 0;
  let exNote = '';
  if (calcMode === 'excess_then_gst') {
    const surplusGross = Math.max(0, example.sales - example.target);
    const surplusExTax = surplusGross / gstFactor;
    exBase = pct >= r.minAchievementPct && surplusGross > 0 ? (surplusExTax * ratePct) / 100 : 0;
    exNote = `(${inr(example.sales)} − ${inr(example.target)}) ÷ ${gstFactor} = ${inr(surplusExTax)} ex-tax surplus × ${ratePct}%`;
  } else if (calcMode === 'excess_over_target') {
    const salesExTax = example.sales / gstFactor;
    const surplus = Math.max(0, salesExTax - example.target);
    exBase = pct >= r.minAchievementPct && surplus > 0 ? (surplus * ratePct) / 100 : 0;
    exNote = `(${inr(example.sales)} ÷ ${gstFactor} − ${inr(example.target)}) = ${inr(surplus)} ex-tax surplus × ${ratePct}%`;
  } else {
    exBase = pct >= r.minAchievementPct && slab ? (example.sales * slab.ratePct) / 100 : 0;
    exNote = `${pct}% achievement → ${slab ? slab.ratePct : 0}% slab`;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200/80 bg-white px-4 py-3">
        <Settings2 className="h-5 w-5 text-blue-600" />
        <p className="text-[13px] text-slate-600">
          <b className="text-slate-900">Version {data.version}</b>{data.version === 0 ? ' (company defaults)' : ''} ·{' '}
          {ro ? 'Only a Super Admin, Admin or Department Administrator can change these.' : 'Changes apply to months not yet sent for approval. Every save is kept with its reason.'}
        </p>
      </div>

      {/* RBM Incentive Calculation Policy Panel */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Panel title="RBM Incentive Formula & Policy" subtitle="Primary incentive calculation model for sales representatives" icon={Sparkles} className="xl:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label="Incentive Calculation Mode" hint="Determines how the baseline incentive is computed for sales reps.">
                <select
                  className={`${inputCls} font-medium text-slate-800`}
                  disabled={ro}
                  value={calcMode}
                  onChange={(e) => set('calcMode', e.target.value as Rules['calcMode'])}
                >
                  <option value="excess_over_target">Ex-Tax Revenue Minus Target (RBM Standard): (Revenue ÷ 1.18 − Target) × 1%</option>
                  <option value="excess_then_gst">Surplus Over Target Ex-Tax: (Revenue − Target) ÷ 1.18 × 1%</option>
                  <option value="slab">Tiered Slabs (% of Sales Commission)</option>
                </select>
              </Field>
            </div>

            <Field label="GST Rate (%)" hint="Used to deduct tax (e.g. 18% yields ÷ 1.18 divisor).">
              {numInput(gstPercent, (n) => set('gstPercent', n))}
            </Field>

            <Field label="Incentive Rate (%)" hint="Applied to eligible surplus (1% = 0.01 multiplier).">
              {numInput(ratePct, (n) => set('incentiveRatePct', n))}
            </Field>

            <div className="sm:col-span-2">
              <Field
                label="Excluded Usernames (Super Admin Client Managers)"
                hint="Comma-separated usernames. These reps can be assigned targets for performance tracking, but are excluded from leaderboards and receive ₹0 incentive."
              >
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-600" />
                  <input
                    className={inputCls}
                    disabled={ro}
                    type="text"
                    placeholder="e.g. bdm6"
                    value={(r.excludedUsernames || []).join(', ')}
                    onChange={(e) =>
                      set(
                        'excludedUsernames',
                        e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                      )
                    }
                  />
                </div>
              </Field>
            </div>
          </div>
        </Panel>

        <Panel title="Live Calculation Preview" subtitle="Test with any target and sales figures" icon={Calculator}>
          <div className="flex flex-col gap-3">
            <Field label="Target (₹)"><input className={inputCls} type="number" value={example.target} onChange={(e) => setExample({ ...example, target: num(e.target.value) })} /></Field>
            <Field label="Gross Sales Achieved (₹)"><input className={inputCls} type="number" value={example.sales} onChange={(e) => setExample({ ...example, sales: num(e.target.value) })} /></Field>
            <div className="rounded-xl bg-gradient-to-br from-blue-50 to-white p-4 ring-1 ring-blue-100">
              <p className="text-xs text-slate-500">Achievement <b className="text-slate-900">{pct}%</b></p>
              <p className="mt-1 text-2xl font-bold text-blue-700">{inr(exBase)}</p>
              <p className="mt-1 text-data-xs font-mono text-slate-600">{exNote}</p>
              <p className="mt-2 text-data-xs text-slate-500">
                {pct < r.minAchievementPct ? `Below the ${r.minAchievementPct}% minimum — ₹0 incentive.` : 'Plus any performance bonuses below.'}
              </p>
            </div>
          </div>
        </Panel>
      </div>

      {/* Slabs Panel (Shown or collapsible if in slab mode) */}
      {(calcMode === 'slab' || showSlabs) && (
        <Panel
          title="Incentive Slabs (Tiered Mode)"
          subtitle={calcMode === 'slab' ? 'Active commission tiers applied to sales' : 'Configured slabs (Inactive while RBM Formula Mode is selected)'}
          icon={Scale}
        >
          <table className="w-full text-[13px]">
            <thead><tr className="text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500"><th className="pb-2">Achievement from %</th><th className="pb-2">up to %</th><th className="pb-2">Incentive %</th><th /></tr></thead>
            <tbody>
              {r.slabs.map((s, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2">{numInput(s.fromPct, (n) => set('slabs', r.slabs.map((x, j) => (j === i ? { ...x, fromPct: n } : x))))}</td>
                  <td className="py-1 pr-2"><input className={inputCls} disabled={ro} type="number" placeholder="and above" value={s.toPct ?? ''} onChange={(e) => set('slabs', r.slabs.map((x, j) => (j === i ? { ...x, toPct: e.target.value === '' ? null : Number(e.target.value) } : x)))} /></td>
                  <td className="py-1 pr-2">{numInput(s.ratePct, (n) => set('slabs', r.slabs.map((x, j) => (j === i ? { ...x, ratePct: n } : x))))}</td>
                  <td className="w-8">{!ro && <button type="button" onClick={() => set('slabs', r.slabs.filter((_, j) => j !== i))} aria-label="Remove slab" className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!ro && <button type="button" className="btn-sp mt-3" onClick={() => set('slabs', [...r.slabs, { fromPct: 0, toPct: null, ratePct: 0 }])}><Plus className="h-4 w-4" /> Add slab</button>}
        </Panel>
      )}

      {calcMode !== 'slab' && !showSlabs && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
          <span>Tiered Commission Slabs are currently hidden (using RBM Formula Mode).</span>
          <button type="button" onClick={() => setShowSlabs(true)} className="font-semibold text-blue-600 hover:text-blue-800">
            Show Slab Settings
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Panel title="Basis & Thresholds" icon={Settings2}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Sales counted on">
              <select className={inputCls} disabled={ro} value={r.salesBasis} onChange={(e) => set('salesBasis', e.target.value as Rules['salesBasis'])}><option value="grand">Invoice total (with GST)</option><option value="taxable">Taxable value (without GST)</option></select>
            </Field>
            <Field label="Sales eligibility">
              <select className={inputCls} disabled={ro} value={r.eligibleSales} onChange={(e) => set('eligibleSales', e.target.value as Rules['eligibleSales'])}><option value="gross">All sales (new + existing)</option><option value="new">New-customer sales only</option></select>
            </Field>
            <Field label="Minimum achievement % for any incentive">{numInput(r.minAchievementPct, (n) => set('minAchievementPct', n))}</Field>
            <Field label="Maximum incentive per month (₹)" hint="0 = no cap">{numInput(r.maxIncentive, (n) => set('maxIncentive', n))}</Field>
            <Field label="Customers added before this date are 'existing'" hint="Clients carried over from the old system are not new business.">
              <input className={inputCls} disabled={ro} type="date" value={r.oldCustomersBefore || ''} onChange={(e) => set('oldCustomersBefore', e.target.value)} />
            </Field>
            <Field label="Close each month automatically on day" hint="Of the next month — incentives are calculated and the month is frozen.">{numInput(r.autoCloseDay ?? 5, (n) => set('autoCloseDay', n))}</Field>
          </div>
        </Panel>

        <Panel title="Bonuses (₹)" icon={Gift}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Fixed amount on reaching the minimum">{numInput(r.fixedIncentive, (n) => set('fixedIncentive', n))}</Field>
            <Field label="Per new customer">{numInput(r.newCustomerBonus, (n) => set('newCustomerBonus', n))}</Field>
            <Field label="Per existing customer re-ordering">{numInput(r.existingCustomerBonus, (n) => set('existingCustomerBonus', n))}</Field>
            <Field label="Per pipeline deal won">{numInput(r.conversionBonus, (n) => set('conversionBonus', n))}</Field>
            <Field label="Top performer of the month">{numInput(r.topPerformerBonus, (n) => set('topPerformerBonus', n))}</Field>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2">
            {(['monthlyBonus', 'quarterlyBonus', 'annualBonus'] as const).map((k) => (
              <div key={k} className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
                <b className="w-20 text-slate-900">{{ monthlyBonus: 'Monthly', quarterlyBonus: 'Quarterly', annualBonus: 'Annual' }[k]}</b>
                pay {numInput(r[k].amount, (n) => set(k, { ...r[k], amount: n }), '!w-28')} when achievement ≥ {numInput(r[k].minPct, (n) => set(k, { ...r[k], minPct: n }), '!w-20')} %
              </div>
            ))}
            <p className="text-data-xs text-slate-400">The quarterly bonus is paid with the quarter's last month; the annual one with March.</p>
          </div>
        </Panel>

        <Panel title="Performance score" subtitle={`Weights must add up to 100 — now ${weightTotal}`} icon={Star}>
          <div className="flex flex-col gap-2">
            {([['achievement', 'Sales achievement'], ['conversion', 'Conversion (win rate)'], ['newCustomer', 'New customer acquisition'], ['retention', 'Retention / existing sales'], ['activity', 'Follow-ups completed']] as const).map(([k, label]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="w-48 text-[13px] text-slate-700">{label}</span>
                <div className="h-2 flex-1 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-blue-500" style={{ width: `${Math.min(100, r.scoreWeights[k])}%` }} /></div>
                {numInput(r.scoreWeights[k], (n) => set('scoreWeights', { ...r.scoreWeights, [k]: n }), '!w-20')}
              </div>
            ))}
            {weightTotal !== 100 && <p className="text-xs font-semibold text-rose-600">The weights add up to {weightTotal}, not 100.</p>}
          </div>
        </Panel>

        <Panel title="Grades" subtitle="Lowest score for each grade" icon={Star}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {r.grades.map((g, i) => (
              <div key={i} className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
                <input className={`${inputCls} !w-16 text-center font-bold`} disabled={ro} value={g.grade} onChange={(e) => set('grades', r.grades.map((x, j) => (j === i ? { ...x, grade: e.target.value } : x)))} />
                <span className="text-xs text-slate-500">from</span>
                {numInput(g.minScore, (n) => set('grades', r.grades.map((x, j) => (j === i ? { ...x, minScore: n } : x))), '!w-20')}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {data.history.length > 0 && (
        <Panel title="Rule history" icon={History}>
          <ul className="flex flex-col gap-1.5 text-[13px]">{data.history.map((h) => <li key={h.version} className="flex gap-3"><b className="w-10 text-slate-900">v{h.version}</b><span className="w-40 text-slate-500">{new Date(h.createdAt).toLocaleString('en-IN')}</span><span className="text-slate-700">{h.note}</span></li>)}</ul>
        </Panel>
      )}

      {!ro && dirty && (
        <SaveBar>
          <span className="text-sm font-semibold text-slate-800">You have unsaved rule changes</span>
          <div className="ml-auto flex gap-2">
            <button type="button" className="btn-sp" onClick={() => setR(structuredClone(data.rules))}>Discard</button>
            <button type="button" className="btn-sp-primary" onClick={() => setConfirm(true)}>Save as version {data.version + 1}</button>
          </div>
        </SaveBar>
      )}
      <Dialog open={confirm} onClose={() => setConfirm(false)} title={`Save rules as version ${data.version + 1}`}
        footer={(
          <>
            <button type="button" className="btn-sp" onClick={() => setConfirm(false)}>Cancel</button>
            <button type="button" className="btn-sp-primary" disabled={save.isPending || !note.trim()} onClick={async () => {
              try { await save.mutateAsync({ rules: r, note }); setNote(''); setConfirm(false); toast.success('Incentive rules saved as a new version.'); }
              catch (err) { toast.error(errText(err, 'Could not save the rules.')); }
            }}>{save.isPending ? 'Saving…' : 'Save rules'}</button>
          </>
        )}>
        <Field label="Why are the rules changing?" hint="Kept with this version in the audit log."><textarea className={inputCls} rows={3} autoFocus value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </Dialog>
    </div>
  );
}

function SaveBar({ children }: { children: ReactNode }) {
  return <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">{children}</div>;
}
